// Theater mode display helpers — shared by the performance picker, the cart
// summary row and the order confirmation modal so all three render the exact
// same label for a performance.

// BCP 47 tags for the six UI languages, only used to render the weekday/date.
export const LANGUAGE_LOCALES = {
  da: 'da-DK',
  no: 'nb-NO',
  en: 'en-GB',
  de: 'de-DE',
  sv: 'sv-SE',
  es: 'es-ES',
};

// The three display parts of a performance: { dayLabel, time, name }. The
// picker lays them out in fixed columns so dates, times and names align
// vertically; the summary rows join them into one string. The weekday is
// computed from the performance's own calendar date at local noon, never from
// the ISO instant, so timezones can't shift it to the wrong day.
export const formatTheaterPerformanceParts = (performance, language) => {
  if (!performance) return { dayLabel: '', time: '', name: '' };

  const locale = LANGUAGE_LOCALES[language] || 'da-DK';
  let dayLabel = performance.Date || performance.date || '';
  const base = new Date(`${performance.Date || performance.date}T12:00:00`);
  if ((performance.Date || performance.date) && !Number.isNaN(base.getTime())) {
    dayLabel = base.toLocaleDateString(locale, {
      weekday: 'short',
      day: '2-digit',
      month: '2-digit',
    });
    dayLabel = dayLabel.charAt(0).toUpperCase() + dayLabel.slice(1);
  }

  return {
    dayLabel,
    time: performance.Start || performance.start || '',
    name: performance.Name || performance.name || '',
  };
};

// How many days of the program the picker shows before the guest asks for the
// rest. A venue may publish a whole season — Folketeatret's runs to June 2027,
// nearly 400 performances — and food is almost always ordered for tonight or
// the next few days, so the near dates are what belong on screen. The rest is
// one tap away, never truncated.
export const THEATER_VISIBLE_DAYS = 7;

// The picker's performances grouped into days: [{ date, performances }], in the
// order the server sent them (already sorted by date, then start time). One
// group per calendar date, so a venue playing four shows on a December Saturday
// renders as one dated heading with four times under it.
export const groupTheaterPerformancesByDay = (performances) => {
  if (!Array.isArray(performances)) return [];

  const groups = [];
  const byDate = new Map();
  performances.forEach((performance) => {
    const date = performance?.date;
    if (!date) return;
    if (!byDate.has(date)) {
      const group = { date, performances: [] };
      byDate.set(date, group);
      groups.push(group);
    }
    byDate.get(date).performances.push(performance);
  });

  return groups;
};

// "I dag · onsdag 19. aug." — the heading above a day's performances. `today`
// and `tomorrow` come from the caller's translations so the words follow the
// UI language; pass them empty to always get the plain date. The date is read
// at local noon (same as formatTheaterPerformanceParts) so no timezone can
// shift the weekday, and "today" is decided on the guest's own calendar date.
export const formatTheaterDayHeading = (
  date,
  language,
  { today = '', tomorrow = '' } = {},
  now = new Date(),
) => {
  if (!date) return '';

  const locale = LANGUAGE_LOCALES[language] || 'da-DK';
  const base = new Date(`${date}T12:00:00`);
  if (Number.isNaN(base.getTime())) return date;

  let label = base.toLocaleDateString(locale, {
    weekday: 'long',
    day: 'numeric',
    month: 'short',
  });
  label = label.charAt(0).toUpperCase() + label.slice(1);

  // Compare calendar days, not instants — a performance at 19:30 tonight is
  // still "today" at 23:00.
  const startOfDay = (value) => new Date(value.getFullYear(), value.getMonth(), value.getDate());
  const dayDelta = Math.round((startOfDay(base).getTime() - startOfDay(now).getTime()) / 86400000);

  if (dayDelta === 0 && today) return `${today} · ${label}`;
  if (dayDelta === 1 && tomorrow) return `${tomorrow} · ${label}`;
  return label;
};

// "Ons. 12/08 — 19:30 Aftenforestilling" — the parts joined into one string
// (cart summary row and order confirmation modal).
export const formatTheaterPerformanceLabel = (performance, language) => {
  if (!performance) return '';
  const { dayLabel, time, name } = formatTheaterPerformanceParts(performance, language);
  const timeAndName = [time, name].filter(Boolean).join(' ');
  return [dayLabel, timeAndName].filter(Boolean).join(' — ');
};
